import { stepDinic } from '../core/DinicSolver';
import { stepEdmondsKarp } from '../core/EdmondsKarpSolver';
import { stepFordFulkerson } from '../core/FordFulkersonSolver';
import { stepGoldbergTarjan } from '../core/GoldbergTarjanSolver';
import { generateProblemWithLocation } from '../core/problem-generator';
import { Edge, MaxFlowProblem } from '../typings';

export type TestResult = {
  noNodes: number;
  maxCapacity: number;
  // 0 = Ford-Fulkerson
  // 1 = Edmonds-Karp
  // 2 = Dinic
  // 3 = Goldberg-Tarjan
  // Keying these does not play nice with the transpiler at other places. :(
  individualResults: {
    runtime: number;
    satisfiesPreservation: {
      ok: boolean;
      negativeSource: boolean;
      positiveDrain: boolean;
      unsatisfyingNodes: number[];
      testTest: boolean;
    };
    satisfiesCapacity: {
      ok: boolean;
      unsatisfyingEdges: {
        source: number;
        target: number;
        maxCapacity: number;
        residualCapacity: number;
      }[];
      testTest: boolean;
    };
    hasSaturatedCut: {
      ok: boolean;
      testTest: boolean;
    };
    // todo: Funktioniert noch nachdem geeignete Kantenwerte geändert wurden
  }[];
  sameFlowValueAcrossAlgorithms: {
    ok: boolean;
    testTest: boolean;
  };
};

export default function runTests(
  configs: {
    noInstances: number;
    noNodes: number;
    maxCapacity: number;
  }[]
): TestResult[] {
  const results = [];
  for (const config of configs) {
    for (let i = 0; i < config.noInstances; i += 1) {
      console.log(
        `Running test ${i + 1} of ${config.noInstances} for config with ${config.noNodes} nodes.`
      );
      results.push(runTest(config.noNodes, config.maxCapacity));
    }
  }
  return results;
}

export function runTest(noNodes: number, maxCapacity: number): TestResult {
  const problem = generateProblemWithLocation(noNodes, maxCapacity);
  const results: {
    algorithm: 0 | 1 | 2 | 3;
    solver: (
      problem: MaxFlowProblem,
      distanceLabels: number[],
      q: number[],
      curEdge: number,
      xf: number[],
      initDone: boolean,
      qHead: number,
      qTail: number
    ) => [
      MaxFlowProblem,
      boolean,
      number[],
      boolean[][],
      boolean[][],
      number[],
      number[],
      number,
      number[],
      boolean,
      number,
      number
    ];
    runtime: number;

    satisfiesPreservation?: {
      ok: boolean;
      negativeSource: boolean;
      positiveDrain: boolean;
      drainBalancesSource: boolean;
      unsatisfyingNodes: number[];
      testTest: boolean;
    };
    satisfiesCapacity?: {
      ok: boolean;
      unsatisfyingEdges: {
        source: number;
        target: number;
        maxCapacity: number;
        residualCapacity: number;
      }[];
      testTest: boolean;
    };
    hasSaturatedCut?: {
      ok: boolean;
      testTest: boolean;
    };
  }[] = [
    {
      algorithm: 0,
      solver: stepFordFulkerson,
      runtime: -1,
      satisfiesPreservation: undefined,
      satisfiesCapacity: undefined,
      hasSaturatedCut: undefined
    },
    {
      algorithm: 1,
      solver: stepEdmondsKarp,
      runtime: -1,
      satisfiesPreservation: undefined,
      satisfiesCapacity: undefined,
      hasSaturatedCut: undefined
    },
    {
      algorithm: 2,
      solver: stepDinic,
      runtime: -1,
      satisfiesPreservation: undefined,
      satisfiesCapacity: undefined,
      hasSaturatedCut: undefined
    },
    {
      algorithm: 3,
      solver: stepGoldbergTarjan,
      runtime: -1,
      satisfiesPreservation: undefined,
      satisfiesCapacity: undefined,
      hasSaturatedCut: undefined
    }
  ];
  const flowValues = [];
  for (const result of results) {
    let p = JSON.parse(JSON.stringify(problem));
    let done = false;
    let augmentedPath: number[] = [];
    let levelEdges = {};
    let blockingEdges = [];
    let distanceLabels: number[] = [];
    let q: number[] = [];
    let curEdge = 0;
    let xf: number[] = [];
    let initDone = false;
    let qHead = 0;
    let qTail = 0;
    const t0 = performance.now();
    while (!done) {
      [
        p,
        done,
        augmentedPath,
        levelEdges,
        blockingEdges,
        distanceLabels,
        q,
        curEdge,
        xf,
        initDone,
        qHead,
        qTail
      ] = result.solver(p, distanceLabels, q, curEdge, xf, initDone, qHead, qTail);
    }
    const t1 = performance.now();
    result.runtime = t1 - t0;
    result.satisfiesPreservation = satisfiesPreservation(p);
    result.satisfiesPreservation.testTest = testSatisfiesPreservation(
      p,
      result.satisfiesPreservation
    );
    result.satisfiesCapacity = satisfiesCapacity(p);
    result.satisfiesCapacity.testTest = testSatisfiesCapacity(p, result.satisfiesCapacity);
    result.hasSaturatedCut = hasSaturatedCut(p);
    result.hasSaturatedCut.testTest = testHasSaturatedCut(p, result.hasSaturatedCut);
    flowValues.push(flowValue(p));
  }

  let sameFlowValueAcrossAlgorithms = true;
  for (let i = 1; i < 4; i += 1) {
    if (flowValues[i - 1] !== flowValues[i]) sameFlowValueAcrossAlgorithms = false;
  }

  const individualResults = {} as any;
  for (const r of results) {
    individualResults[r.algorithm] = {
      runtime: r.runtime,
      satisfiesPreservation: r.satisfiesPreservation,
      satisfiesCapacity: r.satisfiesCapacity,
      hasSaturatedCut: r.hasSaturatedCut
    };
  }
  return {
    noNodes,
    maxCapacity,
    individualResults,
    sameFlowValueAcrossAlgorithms: {
      ok: sameFlowValueAcrossAlgorithms,
      testTest: testSameFlowValue(problem, { ok: sameFlowValueAcrossAlgorithms })
    }
  };
}

function satisfiesPreservation(problem: MaxFlowProblem): {
  ok: boolean;
  negativeSource: boolean;
  positiveDrain: boolean;
  drainBalancesSource: boolean;
  unsatisfyingNodes: number[];
  testTest: boolean;
} {
  const changeAtNodes = Array.from(problem.graph.nodes, () => 0);
  for (let u = 0; u < problem.graph.nodes.length; u += 1) {
    for (const v of problem.graph.cachedTargetKeys[u]) {
      const edge = problem.graph.edges[u][v];
      const change = edge.maxCapacity - edge.residualCapacity;
      changeAtNodes[u] -= change;
      changeAtNodes[v] += change;
    }
  }
  const negativeSource = changeAtNodes[problem.source] < 0;
  const positiveDrain = changeAtNodes[problem.target] > 0;
  const drainBalancesSource = changeAtNodes[problem.source] + changeAtNodes[problem.target] === 0;
  const unsatisfyingNodes = changeAtNodes
    .map((e, i) => {
      return { change: e, index: i };
    })
    .filter((e) => e.change !== 0)
    .map((e) => e.index)
    .filter((e) => (e === problem.source ? !(negativeSource && drainBalancesSource) : true))
    .filter((e) => (e === problem.target ? !(positiveDrain && drainBalancesSource) : true));
  return {
    ok: unsatisfyingNodes.length === 0,
    negativeSource,
    positiveDrain,
    drainBalancesSource,
    unsatisfyingNodes,
    testTest: false // todo
  };
}

function testSatisfiesPreservation(
  p: MaxFlowProblem,
  r: {
    ok: boolean;
    negativeSource: boolean;
    positiveDrain: boolean;
    drainBalancesSource: boolean;
    unsatisfyingNodes: number[];
    testTest: boolean;
  }
): boolean {
  // Verifying that the algorithm does not work with high confidence is of low interest.
  // I want to increase confidence that it works with the test tests.
  if (!r.ok) return true;
  const modifiedP = JSON.parse(JSON.stringify(p)) as MaxFlowProblem;
  let modifiedAnEdge = false;
  let u = 0;
  while (!modifiedAnEdge) {
    u = Math.floor(Math.random() * modifiedP.graph.nodes.length);
    if (u !== modifiedP.source && u !== modifiedP.target) {
      const keys = modifiedP.graph.cachedTargetKeys[u];
      const v = keys[Math.floor(Math.random() * keys.length)];
      modifiedP.graph.edges[u][v].residualCapacity += 1;
      modifiedAnEdge = true;
    }
  }
  if (satisfiesPreservation(modifiedP).ok) return false;
  const sourceKeys = modifiedP.graph.cachedTargetKeys[modifiedP.source];
  for (const v of sourceKeys) {
    modifiedP.graph.edges[modifiedP.source][v].residualCapacity += 1;
    modifiedP.graph.edges[v][modifiedP.source].residualCapacity -= 1;
  }
  if (satisfiesPreservation(modifiedP).drainBalancesSource) return false;
  for (const v of sourceKeys) {
    modifiedP.graph.edges[modifiedP.source][v].residualCapacity =
      modifiedP.graph.edges[modifiedP.source][v].maxCapacity;
    modifiedP.graph.edges[v][modifiedP.source].residualCapacity =
      modifiedP.graph.edges[v][modifiedP.source].maxCapacity;
  }
  if (satisfiesPreservation(modifiedP).negativeSource) return false;
  const targetKeys = modifiedP.graph.cachedTargetKeys[modifiedP.target];
  for (const u of targetKeys) {
    modifiedP.graph.edges[modifiedP.target][u].residualCapacity =
      modifiedP.graph.edges[modifiedP.target][u].maxCapacity;
    modifiedP.graph.edges[u][modifiedP.target].residualCapacity =
      modifiedP.graph.edges[u][modifiedP.target].maxCapacity;
  }
  if (satisfiesPreservation(modifiedP).positiveDrain) return false;
  return true;
}

function satisfiesCapacity(problem: MaxFlowProblem): {
  ok: boolean;
  unsatisfyingEdges: {
    source: number;
    target: number;
    maxCapacity: number;
    residualCapacity: number;
  }[];
  testTest: boolean;
} {
  const unsatisfyingEdges: {
    source: number;
    target: number;
    maxCapacity: number;
    residualCapacity: number;
  }[] = [];
  for (let u = 0; u < problem.graph.nodes.length; u += 1) {
    for (const v of problem.graph.cachedTargetKeys[u]) {
      const edge = problem.graph.edges[u][v];
      const reverseEdge = problem.graph.edges[v][u];
      if (
        edge.maxCapacity < 0 ||
        edge.residualCapacity < 0 ||
        edge.residualCapacity >
          edge.maxCapacity + reverseEdge.maxCapacity - reverseEdge.residualCapacity
      ) {
        unsatisfyingEdges.push({
          source: u,
          target: v,
          ...edge
        });
      }
    }
  }
  return {
    ok: unsatisfyingEdges.length === 0,
    unsatisfyingEdges,
    testTest: false // todo
  };
}

function testSatisfiesCapacity(
  p: MaxFlowProblem,
  r: {
    ok: boolean;
    unsatisfyingEdges: {
      source: number;
      target: number;
      maxCapacity: number;
      residualCapacity: number;
    }[];
    testTest: boolean;
  }
): boolean {
  // Verifying that the algorithm does not work with high confidence is of low interest.
  // I want to increase confidence that it works with the test tests.
  if (!r.ok) return true;
  const modifiedP = JSON.parse(JSON.stringify(p));
  for (let i = 0; i < 5; i += 1) {
    const u = Math.floor(Math.random() * modifiedP.graph.nodes.length);
    const keys = modifiedP.graph.cachedTargetKeys[u];
    const v = keys[Math.floor(Math.random() * keys.length)];
    modifiedP.graph.edges[u][v].residualCapacity =
      modifiedP.graph.edges[u][v].maxCapacity + modifiedP.graph.edges[v][u].maxCapacity + 1;
  }
  if (satisfiesCapacity(modifiedP).ok) return false;
  return true;
}

// Uses max-fow min-cut theorem, because directly checking for a saturated cut is too slow.
// no flow-increasing path <=> has saturated cut
function hasSaturatedCut(problem: MaxFlowProblem): {
  ok: boolean;
  testTest: boolean;
} {
  const q = [problem.source];
  const visited: boolean[] = [];
  visited[problem.source] = true;
  const parents: number[] = [];
  let u = -1;
  let foundAugmentingPath = false;
  while (q.length > 0) {
    u = q.shift() as number;
    if (u === problem.target) {
      foundAugmentingPath = true;
      break;
    } else {
      for (const v of problem.graph.cachedTargetKeys[u]) {
        if (!visited[v] && problem.graph.edges[u][v].residualCapacity > 0) {
          visited[v] = true;
          parents[v] = u;
          q.push(v);
        }
      }
    }
  }
  return {
    ok: !foundAugmentingPath,
    testTest: false
  };
}

function testHasSaturatedCut(
  p: MaxFlowProblem,
  r: {
    ok: boolean;
    testTest: boolean;
  }
): boolean {
  // Verifying that the algorithm does not work with high confidence is of low interest.
  // I want to increase confidence that it works with the test tests.
  if (!r.ok) return true;
  const smallP = generateProblemWithLocation(10, 100, 0);
  for (let u = 0; u < smallP.graph.nodes.length; u += 1) {
    for (const v of smallP.graph.cachedTargetKeys[u]) {
      smallP.graph.edges[u][v].residualCapacity = smallP.graph.edges[u][v].maxCapacity;
    }
  }
  if (hasSaturatedCut(smallP).ok) return false;
  return true;
}

function flowValue(problem: MaxFlowProblem): number {
  let f = 0;
  for (const v of problem.graph.cachedTargetKeys[problem.source]) {
    const outgoingEdge = problem.graph.edges[problem.source][v];
    const incomingEdge = problem.graph.edges[v][problem.source];
    f += outgoingEdge.maxCapacity - outgoingEdge.residualCapacity;
    f -= incomingEdge.maxCapacity - incomingEdge.residualCapacity;
  }
  return f;
}

function testSameFlowValue(
  problem: MaxFlowProblem,
  r: {
    ok: boolean;
  }
): boolean {
  // Verifying that the algorithm does not work with high confidence is of low interest.
  // I want to increase confidence that it works with the test tests.
  if (!r.ok) return true;
  let p = JSON.parse(JSON.stringify(problem));
  const flow = flowValue(p);
  [p] = stepEdmondsKarp(p, [], [], 0, [], true, 0, 0);
  if (flow === flowValue(p)) return false;
  return true;
}
